<template>
    <div class="map">
        <MglMap
            ref="glmap"
            container="map-test"
            :mapStyle="mapStyle" 
            :accessToken="accessToken"
            @load="onMapLoaded"
            :center="center"
            :zoom="18"
        >
            <MglMarker 
                v-for="place in place_list_marker_coords"
                :key="place.id"
                :coordinates="place.coordinates"
            >
                <div slot="marker" :ref="'marker-' + place.id">
                    <div v-show="place.id != place_current_hover_id"
                        class="numberCircle"
                    >{{place.localNumber}}</div>
                    <div v-show="place.id == place_current_hover_id"
                        class="numberCircleHover"
                    >{{place.localNumber}}</div>
                </div>
            </MglMarker>
        </MglMap>
    </div>
</template>

<style scoped>
.map {
    background-color: blanchedalmond;
    /*height: 85vh; */
    height: 100%;
    z-index: 0;
}

.hover {
    z-index: 5;
}

.non-hover {
    z-index: 4;
}

.numberCircle {
    border-radius: 50%;
    width: 55px;
    height: 55px;
    padding: 8px;
    background: #fd6a02;
    border: 5px solid white;
    color: white;
    text-align: center;
    font: 32px Arial, sans-serif;
    display: inline-block;
}

.numberCircleHover {
    border-radius: 50%;
    width: 55px;
    height: 55px;
    padding: 8px;
    background: white;
    border: 5px solid #fd6a02;
    color: #fd6a02;
    text-align: center;
    font: 32px Arial, sans-serif;
    display: inline-block;
}

</style>

<script>
import "mapbox-gl/dist/mapbox-gl.css"
import "v-mapbox/dist/v-mapbox.css"
import Mapbox from "mapbox-gl"
import { MglMap, MglMarker } from "v-mapbox"
import * as turf from "@turf/turf"

export default {
    components: {
        MglMap,
        MglMarker
    },
    data() {
        return {
            //accessToken1: "pk.eyJ1IjoidnZhdmVwYWNrZXQiLCJhIjoiY2tmdTY1b29xMGY4cTJxcWZ5NWR6dmZtMCJ9.cSaiolTgdK8DqCL017yn1Q",
            //mapStyle1: "mapbox://styles/mapbox/streets-v11",
            accessToken: "",
            mapStyle: {
                "version": 8,
                "sources": {
                    "gozhere": {
                        "type": "vector",
                        "tiles": [
                            process.env.VUE_APP_TILE_URL
                        ],
                        "attribution": "<a href=\"https://www.gozhere.com\" target=\"_blank\">© Gozhere</a>",
                        "maxzoom": 20
                    }
                },
                "glyphs": "https://gozhere.com/fonts/{fontstack}/{range}.pbf",
                "layers": [
                    {
                        "id": "background",
                        "type": "background",
                        "paint": {
                            "background-color": "#485E77"
                        }
                    },
                    {
                        "id": "ne_10m_bathymetry_L_0",
                        "type": "fill",
                        "source": "gozhere",
                        "source-layer": "ne_10m_bathymetry_L_0",
                        "paint": {
                            "fill-color": "#5DBCD2"
                        }
                    },
                    {
                        "id": "ne_10m_land",
                        "type": "fill",
                        "source": "gozhere",
                        "source-layer": "ne_10m_land",
                        "paint": {
                            "fill-color": "#EEEDE8"
                        }
                    },
                    {
                        "id": "gis_osm_water_a_free_1",
                        "type": "fill",
                        "source": "gozhere",
                        "source-layer": "gis_osm_water_a_free_1",
                        "paint": {
                            "fill-color": "#AADAFF"
                        }
                    },
                    {
                        "id": "gis_osm_waterways_free_1",
                        "type": "line",
                        "source": "gozhere",
                        "source-layer": "gis_osm_waterways_free_1",
                        "paint": {
                            "line-color": "#AADAFF"
                        }
                    },
                    {
                        "id": "gis_osm_landuse_a_free_1",
                        "type": "fill",
                        "source": "gozhere",
                        "source-layer": "gis_osm_landuse_a_free_1",
                        "paint": {
                            "fill-color": "#DBD8D3"
                        }
                    },
                    {
                        "id": "gis_osm_buildings_a_free_1",
                        "type": "fill",
                        "source": "gozhere",
                        "source-layer": "gis_osm_buildings_a_free_1",
                        "filter": [
                            "in",
                            "fclass",
                            "building"
                        ],
                        "paint": {
                            "fill-color": "#E5E4E2"
                        }
                    },
                    {
                        "id": "green",
                        "type": "fill",
                        "source": "gozhere",
                        "source-layer": "gis_osm_landuse_a_free_1",
                        "filter": [
                            "in",
                            "fclass",
                            "park",
                            "golf_course"
                        ],
                        "paint": {
                            "fill-color": "#C5E6BB"
                        }
                    },
                    {
                        "id": "primary_routes_zoom_0",
                        "type": "line",
                        "source": "gozhere",
                        "source-layer": "gis_osm_roads_free_1",
                        "filter": [
                            "in",
                            "fclass",
                            "primary",
                            "trunk"
                        ],
                        "paint": {
                            "line-color": "#FEDB52",
                            "line-width": 9
                        }
                    },
                    {
                        "id": "secondary_routes_zoom_0",
                        "type": "line",
                        "source": "gozhere",
                        "source-layer": "gis_osm_roads_free_1",
                        "filter": [
                            "in",
                            "fclass",
                            "secondary"
                        ],
                        "paint": {
                            "line-color": "#F4D26E",
                            "line-width": 6
                        }
                    },
                    {
                        "id": "tertiary_routes_zoom_0",
                        "type": "line",
                        "source": "gozhere",
                        "source-layer": "gis_osm_roads_free_1",
                        "filter": [
                            "in",
                            "fclass",
                            "service",
                            "residential",
                            "tertiary",
                            "unclassified"
                        ],
                        "paint": {
                            "line-color": "#FEFEFA",
                            "line-width": 3
                        }
                    },
                    {
                        "id": "gis_osm_water_a_free_1-label",
                        "type": "symbol",
                        "source": "gozhere",
                        "source-layer": "gis_osm_water_a_free_1",
                        "layout": {
                            "text-font": ["Open Sans Regular"],
                            "text-field": ["get", "name"],
                            "text-padding": 4,
                            "text-max-width": 7,
                            "text-size": 18
                        },
                        "paint": {
                            "text-color": "hsl(196, 76%, 50%)",
                            "text-halo-color": "hsl(35, 7%, 90%)",
                            "text-halo-width": 0.5
                        }
                    },
                    {
                        "id": "gis_osm_waterways_free_1-label",
                        "type": "symbol",
                        "source": "gozhere",
                        "source-layer": "gis_osm_waterways_free_1",
                        "layout": {
                            "text-font": ["Open Sans Regular"],
                            "text-field": ["get", "name"],
                            "text-padding": 4,
                            "text-max-width": 7,
                            "text-size": 18
                        },
                        "paint": {
                            "text-color": "hsl(196, 76%, 50%)",
                            "text-halo-color": "hsl(35, 7%, 90%)",
                            "text-halo-width": 0.5
                        }
                    },
                    {
                        "id": "primary_routes_zoom_0-label",
                        "type": "symbol",
                        "source": "gozhere",
                        "source-layer": "gis_osm_roads_free_1",
                        "layout": {
                            "text-size": [
                                "interpolate",
                                ["linear"],
                                ["zoom"],
                                10,
                                [
                                    "match",
                                    ["get", "fclass"],
                                    [
                                        "motorway",
                                        "trunk",
                                        "primary",
                                        "secondary",
                                        "tertiary"
                                    ],
                                    10,
                                    [
                                        "motorway_link",
                                        "trunk_link",
                                        "primary_link",
                                        "secondary_link",
                                        "tertiary_link",
                                        "street",
                                        "street_limited"
                                    ],
                                    9,
                                    6.5
                                ],
                                18,
                                [
                                    "match",
                                    ["get", "fclass"],
                                    [
                                        "motorway",
                                        "trunk",
                                        "primary",
                                        "secondary",
                                        "tertiary"
                                    ],
                                    16,
                                    [
                                        "motorway_link",
                                        "trunk_link",
                                        "primary_link",
                                        "secondary_link",
                                        "tertiary_link",
                                        "street",
                                        "street_limited"
                                    ],
                                    14,
                                    13
                                ]
                            ],
                            "text-max-angle": 30,
                            "text-font": ["Open Sans Regular"],
                            "symbol-placement": "line",
                            "text-padding": 1,
                            "text-rotation-alignment": "map",
                            "text-pitch-alignment": "viewport",
                            "text-field": ["get", "name"],
                            "text-letter-spacing": 0.01
                        },
                        "paint": {
                            "text-color": "hsl(0,0%, 0%)",
                            "text-halo-color": "hsl(35, 19%, 100%)",
                            "text-halo-width": 1,
                            "text-halo-blur": 1
                        }
                    }
                    
                ]
            },
            center: [120.9779, 14.5905],
            zoom: 14,
            currentAttemptCounter: 0,
            maxAttempt: 3
        }
    },
    methods: {
        onMapLoaded(event) {
            console.log('2')
            console.log(event)
            console.log(event.map)
            // event.map.flyTo({
            //      center: [30, 30]
            //  })
            console.log(this.$refs.glmap.map)
            // Previously its GLMap calling the retrieval
            // now it should be other component i.e. Search
            // OLD code below
            // call the place api to load places
            //this.$store.dispatch('retrievePlaces')
            //this.$store.dispatch('retrievePlacesPopular')
            //this.$store.dispatch('retrievePlacesNearYou')
        },
        fitBounds() {
            try {
                var coordinates = this.place_list_marker_coords
                var list = []
                coordinates.forEach(e => {
                    list.push(e.coordinates)
                })
                var multiPt = turf.multiPoint(list)
                var bbox = turf.bbox(multiPt)
                console.log(bbox)
                console.log('moving map bbox')
                this.$nextTick(() => {
                    console.log(this.$refs.glmap)
                    console.log(this.$refs.glmap.map)
                })
                console.log(this.$refs.glmap)
                console.log(this.$refs.glmap.mapStyle)
                console.log(this.$refs.glmap.map)
                //console.log(this.$refs.glmap.actions)
                // this.$refs.map.map.flyTo({
                //      center: [30, 30]
                //  })
                //this.$refs.map.map
                this.$refs.glmap.map.fitBounds(bbox, {
                    padding: 20
                })
            } 
            catch(err) {
                console.error('fitBounds', err)
                var vm = this
                setTimeout(() => {
                    console.log('trying attempt: ' + this.currentAttemptCounter + ' out of ' + this.maxAttempt)
                    if (this.currentAttemptCounter < this.maxAttempt) {
                        console.log('calling fitBounds again with params...', coordinates)
                        this.currentAttemptCounter++
                        vm.fitBounds(coordinates)
                    } else {
                        console.log('fitBounds exhausted')
                    }
                    
                }, 1000)
            }
        }
    },
    computed: {
        place_list_marker_coords: {
            get() {
                return this.$store.state.place_list_marker_coords
            }
        },
        place_current_hover_id: {
            get() {
                return this.$store.state.place_current_hover_id
            }
        }
    },
    watch: {
        place_list_marker_coords(val) {
            console.log('watched value: ')
            console.log(val)
            this.fitBounds()
        }
    },
    mounted() {
        console.log('mounted GLMap')
        // try to fitbounds on existing coords
        this.fitBounds()
    },
    created() {
        this.mapbox = Mapbox;
        this.$root.$on('increaseZIndex', () => {
            console.log('increasing z-index')
            //console.log(this.$refs)
            //console.log(this.$refs['marker-' + this.place_current_hover_id][0])
            console.log(this.$refs['marker-' + this.place_current_hover_id])
            this.$refs['marker-' + this.place_current_hover_id][0].classList.remove('non-hover')
            this.$refs['marker-' + this.place_current_hover_id][0].classList.add('hover')
        });
        this.$root.$on('decreaseZIndex', () => {
            console.log('decreasing z-index')
            //console.log(this.$refs)
            //console.log(this.$refs['marker-' + this.place_current_hover_id][0])
            console.log(this.$refs['marker-' + this.place_current_hover_id])
            this.$refs['marker-' + this.place_current_hover_id][0].classList.remove('hover')
            this.$refs['marker-' + this.place_current_hover_id][0].classList.add('non-hover')
        });
    }
}

</script>